import React from "react";
import { Play, Lock, CheckCircle, Question, FlagCheckered, MapTrifold } from "@phosphor-icons/react";

export const CourseJourneyMap = ({ items, active, onSelect, colorOf, view = "map", onViewChange = () => {} }) => {
  const n = items.length;
  const { perRow, VW, PAD_X, PAD_Y, ROW_H, R } = n <= 15
    ? { perRow: 5, VW: 640, PAD_X: 56, PAD_Y: 44, ROW_H: 96, R: 18 }
    : n <= 40
      ? { perRow: 8, VW: 800, PAD_X: 48, PAD_Y: 40, ROW_H: 84, R: 16 }
      : { perRow: 10, VW: 880, PAD_X: 42, PAD_Y: 36, ROW_H: 76, R: 15 };
  const cols = Math.max(1, Math.min(perRow, items.length));
  const rows = Math.ceil(items.length / perRow);
  const VH = PAD_Y * 2 + (rows - 1) * ROW_H + 16;
  const colW = cols > 1 ? (VW - PAD_X * 2) / (cols - 1) : 0;
  const pos = (i) => {
    const row = Math.floor(i / perRow);
    let col = i % perRow;
    if (row % 2 === 1) col = cols - 1 - col;
    return { x: PAD_X + col * colW, y: PAD_Y + row * ROW_H };
  };
  const completedCount = items.filter((it) => it.completed).length;

  return (
    <div data-testid="course-journey-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h2 className="text-lg font-bold font-display flex items-center gap-2">
          <MapTrifold size={20} weight="fill" color="#CCFF00" className="icon-outline" /> Your Journey
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[#000000]" data-testid="journey-progress-label">
            {completedCount}/{items.length} lessons
          </span>
          <div className="flex rounded-full border border-[#C4C8CE] overflow-hidden">
            {["map", "list"].map((v) => (
              <button key={v} type="button" data-testid={`journey-view-${v}`}
                className="text-xs font-bold px-3 py-1 cursor-pointer border-0"
                style={{ background: view === v ? "#0A0D14" : "#fff", color: view === v ? "#fff" : "#52525B" }}
                onClick={() => onViewChange(v)}>
                {v === "map" ? "Map" : "List"}
              </button>
            ))}
          </div>
        </div>
      </div>
      {view === "list" ? (
        <p className="text-xs text-[#000000] m-0" data-testid="journey-list-hint">
          Map tucked away — use the lesson menu below and jump to any lesson you like. Switch back anytime.
        </p>
      ) : (
      <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full block max-w-3xl mx-auto" role="img" aria-label="Course progress map">
        <defs>
          <linearGradient id="journeyDone" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#00E5FF" />
            <stop offset="1" stopColor="#CCFF00" />
          </linearGradient>
        </defs>
        {items.slice(0, -1).map((it, i) => {
          const a = pos(i), b = pos(i + 1);
          return (
            <path key={`seg-${i}`} d={`M ${a.x} ${a.y} L ${b.x} ${b.y}`} fill="none"
              stroke={it.completed ? "#A3B800" : "#C4C8CE"} strokeWidth={it.completed ? 6 : 4}
              strokeDasharray={it.completed ? undefined : "2 8"} strokeLinecap="round" />
          );
        })}
        {items.map((it, i) => {
          const { x, y } = pos(i);
          const isActive = i === active;
          const last = i === items.length - 1;
          const pc = (colorOf && colorOf(it.pillar)) || "#52525B";
          return (
            <g key={i} data-testid={`journey-node-${i}`}
              onClick={() => it.unlocked && onSelect(i)}
              style={{ cursor: it.unlocked ? "pointer" : "default" }}>
              <title>{`${i + 1}. ${it.title}${it.completed ? " — done ✓" : it.unlocked ? "" : " — locked"}`}</title>
              {isActive && (
                <circle cx={x} cy={y} r={R + 6} fill="none" stroke="#CCFF00" strokeWidth="3">
                  <animate attributeName="r" values={`${R + 4};${R + 11};${R + 4}`} dur="1.7s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.9;0.1;0.9" dur="1.7s" repeatCount="indefinite" />
                </circle>
              )}
              <circle cx={x} cy={y} r={R}
                fill={it.completed ? "url(#journeyDone)" : it.unlocked ? "#FFFFFF" : "#F4F4F5"}
                stroke={it.completed ? "#A3B800" : it.unlocked ? pc : "#C4C8CE"}
                strokeWidth={isActive ? 3 : 2} />
              <g transform={`translate(${x - 9}, ${y - 9})`} pointerEvents="none">
                {it.completed ? <CheckCircle size={18} weight="fill" color="#111827" />
                  : !it.unlocked ? <Lock size={18} weight="fill" color="#A1A1AA" />
                  : last ? <FlagCheckered size={18} weight="fill" color={pc} />
                  : it.skipped ? <Question size={18} weight="bold" color="#B45309" />
                  : <Play size={18} weight="fill" color={pc} />}
              </g>
              <text x={x} y={y + R + 15} textAnchor="middle" fontSize="10" fontWeight="700"
                fill={isActive ? "#0A0D14" : "#71717A"}>{i + 1}</text>
            </g>
          );
        })}
      </svg>
      )}
    </div>
  );
};
